/**
 * The explainer (ADR-034): for one person, what the access decision answers
 * for each of the app's workspace collections — can they view it, can they
 * change it, and why. The server runs the decision itself, as that person's
 * own request would; this only puts its reasons into words, so it cannot
 * disagree with what actually happens.
 */
import type {
  AppAccessActionExplanationDto,
  AppAccessAudienceDto,
  AppAccessDecisionCheckDto,
  AppAccessPermissionDto,
  WorkspaceAppRoleDto,
} from '@/lib/api';
import { useAppAccessExplanation, type AppAccessScope } from '../../hooks/useAppAccess';
import { audienceLabel, describeAppAccessError, permissionLabel, roleLabel } from './shared';

interface ExplainerPanelProps {
  scope: AppAccessScope;
  principalId: string;
  /** "you" or the person's name. */
  who: string;
  appName: string;
  roles: readonly WorkspaceAppRoleDto[];
  permissions: readonly AppAccessPermissionDto[];
  /** The app's audiences, for their labels (phase 2); ids are shown without them. */
  audiences?: readonly AppAccessAudienceDto[];
}

/**
 * Which records of a collection with audiences the answer covers (phase 2):
 * the audiences the person reads, or may file into and change records of —
 * the decision's own sets, put into words.
 */
function audienceLine(
  answer: AppAccessActionExplanationDto,
  action: 'read' | 'write',
  audiences: readonly AppAccessAudienceDto[],
): string | null {
  if (!answer.allowed) return null;
  const check = answer.checks.find(
    (c): c is Extract<AppAccessDecisionCheckDto, { check: 'app_access' }> =>
      c.check === 'app_access',
  );
  const sets = check?.audiences;
  if (!sets) return null;
  if (sets.kind === 'unassigned') {
    return 'Only records from before roles were on, which are in no audience yet.';
  }
  const list = (ids: readonly string[]) =>
    ids.map((id) => `“${audienceLabel(audiences, id)}”`).join(', ');
  if (action === 'read') {
    return sets.readable.length > 0
      ? `Sees records placed for ${list(sets.readable)} — no others.`
      : 'Sees none of its records: none of their roles reads any of its audiences.';
  }
  const parts = [
    sets.disclosable.length > 0
      ? `May file new records for ${list(sets.disclosable)}`
      : 'May file new records for no audience',
    sets.overwritable.length > 0
      ? `may change records of ${list(sets.overwritable)}`
      : 'may change none',
  ];
  return `${parts.join('; ')}.`;
}

/** One sentence for the step that decided — the last check the decision ran. */
function because(
  answer: AppAccessActionExplanationDto,
  ctx: Pick<ExplainerPanelProps, 'roles' | 'permissions' | 'appName'>,
): string {
  const last: AppAccessDecisionCheckDto | undefined = answer.checks[answer.checks.length - 1];
  if (!last) return answer.allowed ? 'Allowed.' : 'Not allowed.';
  switch (last.check) {
    case 'workspace_id':
      return 'Not allowed: the request named no workspace.';
    case 'membership':
      return 'Not allowed: not an active member of this workspace.';
    case 'role_floor':
      return answer.allowed
        ? `Allowed by workspace role (${last.role}).`
        : `Not allowed: needs at least the ${last.requiredRole} workspace role (has ${last.role}).`;
    case 'audience':
      return answer.allowed
        ? 'Allowed.'
        : last.reason === 'not_in_roles_mode'
          ? 'Not allowed yet: records are placed in audiences only once roles are on here.'
          : 'Not allowed: none of their roles reads that audience.';
    case 'app_access': {
      const perm = last.permission ? `“${permissionLabel(ctx.permissions, last.permission)}”` : '';
      switch (last.reason) {
        case 'permission_granted': {
          const from = (last.roles ?? [])
            .filter((r) => r.eligible && last.permission && r.permissions.includes(last.permission))
            .map((r) => roleLabel(ctx.roles, r.roleId));
          return `Allowed: ${perm} comes from ${from.join(', ') || 'a role'}.`;
        }
        case 'permission_missing': {
          const ineligible = (last.roles ?? []).filter((r) => !r.eligible);
          return ineligible.length > 0
            ? `Not allowed: needs ${perm}. ${ineligible
                .map((r) => roleLabel(ctx.roles, r.roleId))
                .join(', ')} would give it, but their workspace role is too low for it.`
            : `Not allowed: needs ${perm}, which none of their roles gives.`;
        }
        case 'legacy_tier_rules':
          return answer.allowed
            ? 'Allowed by workspace role — roles are not turned on for this app here yet.'
            : 'Not allowed by workspace role.';
        case 'legacy_born_protected':
          return 'Not allowed yet: this data needs the app’s roles, which are not turned on here.';
        case 'unprotected_in_roles_mode':
          return 'Not allowed: with roles on, this collection must name a permission, and it names none.';
        case 'no_policy':
          return 'Not allowed: the app declares no roles that could give it.';
        case 'org_workspace_only':
          return 'Not allowed: roles apply only in a shared workspace.';
        case 'resolver_absent':
          return 'Not allowed: access could not be checked.';
        case 'unprotected':
          return 'Allowed by workspace role.';
        case 'audiences_being_placed':
          return 'Not allowed yet: roles are being turned on and this data’s records are still being placed.';
        default:
          return answer.allowed ? 'Allowed.' : 'Not allowed.';
      }
    }
  }
}

export function ExplainerPanel(props: ExplainerPanelProps) {
  const explanation = useAppAccessExplanation(props.scope, props.principalId);
  if (explanation.isLoading) return <p className="text-sm text-muted-foreground">Checking…</p>;
  if (explanation.isError) {
    return <p className="text-sm text-destructive">{describeAppAccessError(explanation.error)}</p>;
  }
  const data = explanation.data;
  if (!data || data.collections.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        {props.appName} has no shared data in this workspace yet, so there is nothing to explain.
      </p>
    );
  }
  return (
    <div className="space-y-2 text-sm">
      <p className="text-muted-foreground">
        What {props.who} can do with each kind of {props.appName} data here, as Fias decides it on
        every request.
      </p>
      <ul className="space-y-2">
        {data.collections.map((c) => (
          <li key={c.collection} className="rounded-md border border-border p-2">
            <p className="font-mono text-xs text-foreground">{c.collection}</p>
            <p>
              <span className="font-medium">View:</span> {because(c.read, props)}{' '}
              {audienceLine(c.read, 'read', props.audiences ?? [])}
            </p>
            <p>
              <span className="font-medium">Change:</span>{' '}
              {c.write.createOnly
                ? 'May add new entries, but not change or delete existing ones — that needs viewing too.'
                : because(c.write, props)}{' '}
              {audienceLine(c.write, 'write', props.audiences ?? [])}
            </p>
          </li>
        ))}
      </ul>
    </div>
  );
}
